import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, useSearchParams } from 'react-router-dom';
import Layout from 'src/components/Layout';
import CheckMaintenance from 'src/components/Maintenance/CheckMaintenance';
import LayoutPublic from 'src/components/LayoutPublic';
import LoadingPage from 'src/components/LoadingPage';
import AccessDenied from 'src/screens/access-denied';
import { RootState } from 'src/store';
import { loadUser } from 'src/store/auth/reducer';
import { getProjectRetainer } from 'src/store/projectRetainer/reducer';
import { setSentryUser } from 'src/sentry';
import AppTourProvider from 'src/context/appTourContext';
import { getCurrentCustomer } from 'src/utils/localStorage';
import { getChecklists, getRequestForms } from 'src/utils/jsonCache';
import {
  setResources,
  setResourcesLoading,
} from 'src/store/resources/resourcesSlice';

interface AuthenticationRouteProps {
  isAuth?: boolean | null;
  allowInMaintenance?: boolean;
  noLayout?: boolean;
  layoutPublic?: boolean;
}

const AuthenticationRoute: FC<AuthenticationRouteProps> = ({
  isAuth = null,
  allowInMaintenance = false,
  noLayout = false,
  layoutPublic = false,
}) => {
  const { user, loading } = useSelector(
    (globalState: RootState) => globalState.auth
  );
  const projectRetainer = useSelector(
    (globalState: RootState) => globalState.projectRetainer
  );

  const [searchParams] = useSearchParams();

  const isMountedRef = useRef(false);
  const retainerLoaded = useRef(false);
  const dispatch = useDispatch();

  const [customerUuid, setCustomerUuid] = useState<string | undefined>(
    undefined
  );

  const resoucesLoading = useSelector(
    (state: RootState) => state.resources.loading
  );

  // Only block the screen on the first preload. Later refreshes (triggered by any user or
  // retainer update) run in the background, otherwise the loader unmounts the current page
  // and wipes its local state, e.g. sending the onboarding wizard back to its first step.
  const resourcesLoaded = useRef(false);

  useEffect(() => {
    const currentCustomer = getCurrentCustomer();

    if (currentCustomer) {
      setCustomerUuid(currentCustomer?.uuid);
    }
  }, [user]);

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      dispatch(loadUser());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user && !retainerLoaded.current) {
      retainerLoaded.current = true;
      dispatch(getProjectRetainer());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user && projectRetainer.data) {
      dispatch(setResourcesLoading(true));

      const preloadResources = async () => {
        const [checklists, requestForms] = await Promise.all([
          getChecklists(),
          getRequestForms(),
        ]);

        resourcesLoaded.current = true;
        dispatch(setResources({ checklists, requestForms }));
      };

      preloadResources();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, projectRetainer.data]);

  if (
    (loading && !user) ||
    (projectRetainer.loading && !projectRetainer.data) ||
    (resoucesLoading && !resourcesLoaded.current)
  ) {
    return <LoadingPage />;
  }

  if (user && isAuth === false) {
    return <Navigate replace to="/dashboard" />;
  }

  if (!user && isAuth === true) {
    return <Navigate replace to="/login" />;
  }

  const allowedAdminEmails = [
    'marcelo@savageglobalent.com',
    'santiago@savageglobalent.com',
  ];

  if (user && isAuth === true && user.contact?.type !== 'contact') {
    const isAllowedAdmin = allowedAdminEmails.includes(user.email);
    if (!isAllowedAdmin) {
      return <AccessDenied />;
    }
  }

  if (searchParams.has('task_id')) {
    if (searchParams.get('task_id') === 'create') {
      return <Navigate to="/requests/create" />;
    }

    return <Navigate to="/dashboard" />;
  }

  setSentryUser(user);

  if (layoutPublic) {
    return (
      <CheckMaintenance user={user} allowInMaintenance={allowInMaintenance}>
        <LayoutPublic>
          <Outlet />
        </LayoutPublic>
      </CheckMaintenance>
    );
  }

  if (!user || noLayout) {
    return (
      <CheckMaintenance user={user} allowInMaintenance={allowInMaintenance}>
        <Outlet />
      </CheckMaintenance>
    );
  }

  return (
    <CheckMaintenance user={user} allowInMaintenance={allowInMaintenance}>
      <AppTourProvider>
        <Layout key={customerUuid}>
          <Outlet />
        </Layout>
      </AppTourProvider>
    </CheckMaintenance>
  );
};

export default AuthenticationRoute;
